import React, { useState, useMemo, useCallback } from 'react';
import Header from './Header';
import JobDetails from '../jobDetails/JobDetails';
import ProfileStep from './steps/ProfileStep';
import ContactStep from './steps/ContactStep';
import ResumeStep from './steps/ResumeStep';
import QuestionStep from './steps/QuestionStep';
import FinalizeStep from './steps/FinalizeStep';
import { DefaultDocument } from 'components/general/document_uploader/UploaderStates';
import { getCSRFToken } from 'lib/request';
import WorkPermitStep from './steps/WorkPermitStep';
import ShareWorkPermitStep from './steps/ShareWorkPermitStep';

type StepType = 'profile' | 'contact' | 'work_permit' | 'share_work_permit' | 'resume' | 'questions' | 'finalize';

interface Question {
  id: number;
  mandatory: boolean;
  question_text: string;
  answer_text: string | null;
}

interface PageProps {
  jobAd: any;
  isUserAuthenticated: boolean;
  profile?: any;
  citizenships?: string[];
  workPermit?: boolean;
  workPermitKinds?: { label: string; value: string }[];
  applicationWorkPermit: any;
  guestCv?: DefaultDocument;
  profileCvs?: DefaultDocument[];
  selectedCvId?: string;
  currentStep?: number;
  totalSteps?: number;
  initialStep?: string;
  initialQuestion?: number;
  initialQuestions?: Question[];
}

const Page: React.FC<PageProps> = ({
  jobAd,
  isUserAuthenticated,
  profile,
  citizenships,
  workPermit,
  workPermitKinds,
  applicationWorkPermit,
  guestCv,
  profileCvs,
  selectedCvId,
  currentStep,
  totalSteps,
  initialStep,
  initialQuestion,
  initialQuestions,
}) => {
  const styles = require('./Page.module.scss');

  // Initialize state from backend props
  const initialAnswers = useMemo(() => {
    if (!initialQuestions) return {};
    
    const answers: Record<number, string> = {};
    initialQuestions.forEach((q) => {
      if (q.answer_text) {
        answers[q.id] = q.answer_text;
      }
    });
    return answers;
  }, [initialQuestions]);

  const [profileData, setProfileData] = useState({
    email: profile?.email || '',
    fullName: profile?.full_name || '',
    phone: profile?.phone_number || '',
    city: profile?.city || '',
    country: profile?.country || '',
    countryName: profile?.country_name || '',
    citizenships: profile?.citizenships
      ? profile.citizenships.map((c: string) => ({ value: c, label: c }))
      : [],
  });

  const [workPermitData, setWorkPermitData] = useState({
    kind: applicationWorkPermit?.kind || '',
    number: applicationWorkPermit?.number || '',
    expiresAt: applicationWorkPermit?.expires_at || '',
    notes: applicationWorkPermit?.notes || '',
    issuedCity: applicationWorkPermit?.city || '',
    issuedCountry: applicationWorkPermit?.country_code || '',
    issuedGooglePlaceId: applicationWorkPermit?.google_place_id || '',
    issuedCountryName: applicationWorkPermit?.country_name || '',
  });

  const [resumeState, setResumeState] = useState<{
    uploadedCv?: DefaultDocument;
    selectedCv?: DefaultDocument;
    profileCvs?: DefaultDocument[];
  }>({
    uploadedCv: guestCv,
    selectedCv: profileCvs?.find(cv => cv.id === selectedCvId),
    profileCvs: profileCvs || [],
  });
  
  const [navigationState, setNavigationState] = useState({
    optionWorkPermit: ['resume', 'questions', 'finalize'].includes(initialStep || '') ? true : null,
    needWorkPermit: workPermit || false,
    currentStepType: (initialStep as StepType) || 'profile',
    currentQuestionIndex: initialQuestion && initialQuestion > 0 ? initialQuestion - 1 : 0,
    displayStep: currentStep || 1,
    displayTotalSteps: totalSteps || 1,
  });

  const [questions, setQuestions] = useState<Question[]>(initialQuestions || []);
  const [answers, setAnswers] = useState<Record<number, string>>(initialAnswers);

  const { currentStepType, currentQuestionIndex, displayStep, displayTotalSteps } = navigationState;

  const goToNextStep = useCallback((updatedNeedWorkPermit?: boolean): void => {
    setNavigationState(prev => {
      const newState = { ...prev, displayStep: prev.displayStep + 1 };
      
      switch (prev.currentStepType) {
        case 'profile':
          newState.currentStepType = 'contact';
          break;
        
        case 'contact':
          newState.currentStepType = updatedNeedWorkPermit !== undefined ? 
            (updatedNeedWorkPermit ? 'work_permit' : 'resume') : 
            (prev.needWorkPermit ? 'work_permit' : 'resume');
          
          if (updatedNeedWorkPermit !== undefined) {
            if (updatedNeedWorkPermit !== prev.needWorkPermit) {
              newState.displayTotalSteps = updatedNeedWorkPermit ? 
                prev.displayTotalSteps + 2 : 
                prev.displayTotalSteps - 2;
            }
            newState.needWorkPermit = updatedNeedWorkPermit;
          }
          break;
        
        case 'work_permit':
          newState.currentStepType = 'share_work_permit';
          break;
        
        case 'share_work_permit':
          newState.currentStepType = 'resume';
          break;
        
        case 'resume':
          if (questions.length > 0) {
            newState.currentStepType = 'questions';
            newState.currentQuestionIndex = 0;
          } else if (!isUserAuthenticated) {
            newState.currentStepType = 'finalize';
          }
          break;
        
        case 'questions':
          if (prev.currentQuestionIndex < questions.length - 1) {
            newState.currentQuestionIndex = prev.currentQuestionIndex + 1;
          } else if (!isUserAuthenticated) {
            newState.currentStepType = 'finalize';
          }
          break;
      }
      
      return newState;
    });
  }, [questions.length, isUserAuthenticated]);

  const goToPreviousStep = useCallback((): void => {
    setNavigationState(prev => {
      const newState = { ...prev, displayStep: prev.displayStep - 1 };
      
      switch (prev.currentStepType) {
        case 'contact':
          newState.currentStepType = 'profile';
          break;

        case 'work_permit':
          newState.currentStepType = 'contact';
          break;
        
        case 'share_work_permit':
          newState.currentStepType = 'work_permit';
          break;
        
        case 'resume':
          newState.currentStepType = prev.needWorkPermit ? 'share_work_permit' : 'contact';
          break;
        
        case 'questions':
          if (prev.currentQuestionIndex > 0) {
            newState.currentQuestionIndex = prev.currentQuestionIndex - 1;
          } else {
            newState.currentStepType = 'resume';
          }
          break;
        
        case 'finalize':
          if (questions.length > 0) {
            newState.currentStepType = 'questions';
            newState.currentQuestionIndex = questions.length - 1;
          } else {
            newState.currentStepType = 'resume';
          }
          break;
      }
      
      return newState;
    });
  }, [questions.length, isUserAuthenticated]);

  const handleEmailSuccess = useCallback((emailValue: string): void => {
    setProfileData(prev => ({ ...prev, email: emailValue }));
    goToNextStep();
  }, [goToNextStep]);

  const handleContactSuccess = useCallback((contactForm: any, needsWorkPermit: boolean, questionsData?: Question[]): void => {
    setProfileData(contactForm);
    
    if (questionsData?.length) {
      const newAnswers: Record<number, string> = {};
      questionsData.forEach((q) => {
        if (q.answer_text) {
          newAnswers[q.id] = q.answer_text;
        }
      });
      
      setQuestions(questionsData);
      setAnswers(prev => ({ ...prev, ...newAnswers }));
    }
    
    goToNextStep(needsWorkPermit);
  }, [goToNextStep]);

  const handleWorkPermit = useCallback((hasWorkPermit: boolean): void => {
    if (!hasWorkPermit) {
      window.location.href = window.Routes.ineligible_job_job_ad_quick_applications_url(jobAd.slug);
      return;
    }

    setNavigationState(prev => ({ ...prev, optionWorkPermit: hasWorkPermit }));
    goToNextStep();
  }, [goToNextStep]);

  const handleWorkPermitSuccess = useCallback((workPermitData: any): void => {
    setWorkPermitData(workPermitData);
    goToNextStep();
  }, [goToNextStep]);

  const handleResumeSuccess = useCallback((): void => {
    if (isUserAuthenticated && questions.length === 0) {
      window.location.href = window.Routes.confirmation_job_ad_quick_applications_url(jobAd.slug);
      return;
    }
    goToNextStep();
  }, [isUserAuthenticated, questions.length, goToNextStep, jobAd.slug]);

  const handleQuestionSubmit = useCallback((
    questionId: number,
    answer: string,
    isLastQuestion: boolean = false
  ): void => {
    setAnswers(prev => ({ ...prev, [questionId]: answer }));

    fetch(`/jobs/${jobAd.slug}/quick_apply/questions/${questionId}/answer`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': getCSRFToken() || '',
      },
      body: JSON.stringify({ application_question: { answer_text: answer } }),
    })
    .then(response => response.json().then(data => ({ response, data })))
    .then(({ response, data }) => {
      if (response.ok && data.success) {
        if (isLastQuestion) {
          window.location.href = window.Routes.confirmation_job_ad_quick_applications_url(jobAd.slug);
          return;
        }
        goToNextStep();
      }
    });
  }, [jobAd.slug, goToNextStep]);

  const renderStep = () => {
    switch (currentStepType) {
      case 'profile':
        if (isUserAuthenticated) return null;
        return (
          <ProfileStep
            jobId={jobAd.slug}
            initialEmail={profileData.email}
            onNext={handleEmailSuccess}
          />
        );

      case 'contact':
        return (
          <ContactStep
            jobId={jobAd.slug}
            profile={profileData}
            citizenships={citizenships}
            onNext={handleContactSuccess}
            onPrevious={isUserAuthenticated ? undefined : goToPreviousStep}
          />
        );

      case 'work_permit':
        return (
          <WorkPermitStep
            optionWorkPermit={navigationState.optionWorkPermit}
            onPrevious={!isUserAuthenticated || !profileData.fullName || !profileData.city || !profileData.country || profileData.citizenships.length === 0 ? goToPreviousStep : undefined}
            onNext={handleWorkPermit}
          />
        );

      case 'share_work_permit':
        return (
          <ShareWorkPermitStep
            jobId={jobAd.slug}
            applicationWorkPermit={workPermitData}
            workPermitKinds={workPermitKinds}
            onNext={handleWorkPermitSuccess}
            onPrevious={goToPreviousStep}
          />
        );

      case 'resume':
        return (
          <ResumeStep
            jobId={jobAd.slug}
            isUserAuthenticated={isUserAuthenticated}
            resumeState={resumeState}
            setResumeState={setResumeState}
            onNext={handleResumeSuccess}
            onPrevious={!isUserAuthenticated || navigationState.needWorkPermit ? goToPreviousStep : undefined}
            isLastStep={isUserAuthenticated && questions.length === 0}
          />
        );

      case 'questions':
        const currentQuestion = questions[currentQuestionIndex];
        if (!currentQuestion) return null;

        return (
          <QuestionStep
            key={currentQuestion.id}
            question={currentQuestion}
            questionNumber={currentQuestionIndex + 1}
            initialAnswer={answers[currentQuestion.id] || ''}
            onNext={handleQuestionSubmit}
            onPrevious={goToPreviousStep}
            isLastQuestion={currentQuestionIndex === questions.length - 1 && isUserAuthenticated}
          />
        );

      case 'finalize':
        return (
          <FinalizeStep
            email={profileData.email}
            jobId={jobAd.slug}
            onPrevious={goToPreviousStep}
          />
        );

      default:
        return null;
    }
  };

  const getStepTitle = (): string => {
    switch (currentStepType) {
      case 'profile':
      case 'contact':
        return "Let's get your application started";
      case 'work_permit':
        return `Do you have a valid work permit for ${jobAd.country}?`;
      case 'share_work_permit':
        return "Share your work permit details";
      case 'resume':
        return 'Share your resume';
      case 'questions':
        return 'The company would like to know...';
      case 'finalize':
        return 'Time to set your password';
      default:
        return '';
    }
  };

  const getStepDescription = (): string => {
    if (currentStepType === 'resume') {
      return resumeState.selectedCv || resumeState.uploadedCv || (resumeState.profileCvs?.length ?? 0) > 0
        ? 'Adding or replacing your resume here updates your profile automatically.'
        : 'No CV ready? You can export your LinkedIn profile as PDF';
    }
    return '';
  };

  return (
    <div className={styles.page}>
      <div className={styles.fixedHeader}>
        <Header
          jobId={jobAd.slug}
          jobTitle={jobAd.title}
          jobCompanyId={jobAd.company_id}
          currentStep={displayStep}
          totalSteps={displayTotalSteps}
        />

        <JobDetails jobAd={jobAd} />
      </div>

      <div className={styles.container}>
        <div className={styles.content}>
          <div className={styles.stepWrapper}>
            <div className={styles.stepHeader}>
              <h2 className={styles.title}>{getStepTitle()}</h2>
              {getStepDescription() && (
                <p className={styles.description}>{getStepDescription()}</p>
              )}
            </div>
            {renderStep()}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Page;
